import {statusLabel} from './attendanceStatusTheme'
import {AttendanceStatusSegment} from './buildAttendanceVisualSummary'

export type AttendanceRingCenter = {value: string; label: string; isPercent: boolean}

/** The figure in the middle of the ring, shared by the on-screen ring and the exported chart. */
export function resolveRingCenter(options: {
  segments: AttendanceStatusSegment[]
  presentPercent: number | null
  totalRecords: number
  headlineLabel?: string | null
}): AttendanceRingCenter {
  const {segments, presentPercent, totalRecords, headlineLabel} = options
  const hasPresent = segments.some((s) => s.key === 'present' || s.key === 'excused')
  // A result filtered to a single status draws one full-circle arc, so any rate off it is trivially
  // 100% or 0%: a list of the 96 students present read "100% attendance", which is not the school's
  // attendance rate and not something the query measured. Chart the count and name the status.
  const soleSegment = segments.length === 1 ? segments[0] : null
  // A named headline is the figure the question asked for, so it outranks the present rate — the
  // ring may now chart every status, and without this an "absences" answer would centre on the
  // present percentage instead of the absence count.
  const showPercent = !headlineLabel && !soleSegment && presentPercent !== null && hasPresent

  // "attendance", not "present": the figure is the backend's attendance rate, which credits Late as
  // attended and divides by the marks actually recorded (101/117 today). Present alone would be a
  // different number (96/117), so labelling this one "present" states a figure the data never gave.
  if (showPercent) return {value: `${presentPercent}%`, label: 'attendance', isPercent: true}
  if (headlineLabel) return {value: String(totalRecords), label: headlineLabel, isPercent: false}
  if (soleSegment) {
    return {
      value: String(soleSegment.count),
      label: statusLabel(soleSegment.label),
      isPercent: false,
    }
  }
  // A bare "records" makes the ring read as the size of the range searched.
  return {value: String(totalRecords), label: 'records', isPercent: false}
}
